import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    Chip,
    Switch,
    FormControlLabel,
    InputAdornment,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { Add, Edit, Delete, Search, Visibility, PersonAdd } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';


const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface Tenant {
    id: number;
    name: string;
    email: string;
    phone: string;
    is_active: boolean;
    has_active_subscription: boolean;
    created_at: string;
}

interface TenantFormData {
    name: string;
    email: string;
    phone: string;
    is_active: boolean;
}

const initialFormData: TenantFormData = {
    name: '',
    email: '',
    phone: '',
    is_active: true,
};

const Tenants = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [formData, setFormData] = useState<TenantFormData>(initialFormData);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);

    // Fetch tenants
    const { data, isLoading } = useQuery({
        queryKey: ['tenants', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/tenants', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data: TenantFormData) => {
            if (selectedTenant) {
                return api.put(`/superadmin/tenants/${selectedTenant.id}`, data);
            }
            return api.post('/superadmin/tenants', data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to save tenant');
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/tenants/${id}`),
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to delete tenant');
        },
    });

    const handleOpen = (tenant?: Tenant) => {
        if (tenant) {
            setSelectedTenant(tenant);
            setFormData({
                name: tenant.name,
                email: tenant.email,
                phone: tenant.phone,
                is_active: tenant.is_active,
            });
        } else {
            setSelectedTenant(null);
            setFormData(initialFormData);
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedTenant(null);
        setFormData(initialFormData);
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (tenant: Tenant) => {
        setTenantToDelete(tenant);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (tenantToDelete) {
            deleteMutation.mutate(tenantToDelete.id);
        }
        setDeleteDialogOpen(false);
        setTenantToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTenantToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
        { field: 'phone', headerName: 'Phone', width: 130 },
        {
            field: 'has_active_subscription',
            headerName: 'Subscription',
            width: 130,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.value ? 'success' : 'default'}
                    variant={params.value ? 'filled' : 'outlined'}
                />
            ),
        },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.value ? 'success' : 'error'}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'orange' }} >
                        <BootstrapTooltip title="View Details" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => navigate(`/super-admin/tenants/${params.row.id}`)}>
                                <Visibility fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip >
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }} >
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }} >
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} color="error" onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        {
            field: 'has_active_subscription',
            headerName: 'Sub',
            width: 70,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'None'}
                    size="small"
                    color={params.value ? 'success' : 'default'}
                    variant={params.value ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                />
            ),
        },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 80,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value ? 'Active' : 'Inactive'} size="small" color={params.value ? 'success' : 'error'} />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => navigate(`/super-admin/tenants/${params.row.id}`)}>
                        <Visibility fontSize="small" />
                    </IconButton>

                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>

                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const totalRows = data?.data?.total || data?.total || 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">Tenants</Typography>

            </Box>

            {/* Search */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', borderRadius: 1 }}
                        size="small"
                        placeholder="Search tenants..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button variant="contained" startIcon={<PersonAdd />} onClick={() => handleOpen()}>
                        {isMobile ? 'Add' : 'Add Tenant'}
                    </Button>
                </CardContent>
            </Card>

            {error && !open && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                    <DataGrid
                        rows={rows}
                        columns={isMobile ? mobileColumns : columns}
                        loading={isLoading}
                        rowCount={totalRows}
                        paginationModel={pageModel}
                        onPaginationModelChange={setPageModel}
                        paginationMode="server"
                        pageSizeOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick
                        autoHeight
                        sx={{ border: 0 }}
                    />
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedTenant ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <TextField
                            label="Tenant Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label="Phone"
                            fullWidth
                            required
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                            }}
                            sx={{ mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    bgcolor: '#fee2e2',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: '1px solid #fecaca'
                }}>
                    <Box sx={{
                        bgcolor: '#dc2626',
                        borderRadius: '50%',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                            Confirm Delete
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>
                            This action cannot be undone
                        </Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{tenantToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{
                        bgcolor: '#fef3c7',
                        border: '1px solid #fcd34d',
                        '& .MuiAlert-icon': { color: '#d97706' }
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: You will not be able to revert this action!
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            All associated data including users, subscriptions, and settings will be permanently deleted.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button
                        variant="outlined"
                        onClick={handleDeleteCancel}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteMutation.isPending}
                        startIcon={<Delete />}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#dc2626',
                            '&:hover': { bgcolor: '#b91c1c' }
                        }}
                    >
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete Tenant'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Tenants;
